import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger
} from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { SimpleStrategyDialog } from './SimpleStrategyDialog';
import { SimpleStrategyWizard } from './SimpleStrategyWizard';
import { StepBasedStrategyDialog } from './StepBasedStrategyDialog';
import { ErrorBoundary } from '@/components/ui/errorBoundary';

interface StrategyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => void;
  initialData?: any;
  isStewieQuant?: boolean;
}

export const StrategyDialog = ({ open, onOpenChange, onSubmit, initialData, isStewieQuant }: StrategyDialogProps) => {
  // Initial state for dialog chooser
  const [dialogType, setDialogType] = React.useState<'chooser' | 'simple' | 'advanced'>('chooser');
  
  // Simple dialog is temporarily disabled
  
  // Reset dialog type when main dialog is closed
  React.useEffect(() => {
    if (!open) {
      // Use setTimeout to avoid state updates during render
      setTimeout(() => {
        setDialogType('chooser');
      }, 0);
    }
  }, [open]);
  
  // If showing the simple dialog directly, pass through the props
  if (dialogType === 'simple') {
    // Use the simplified wizard to avoid React Error #185
    return (
      <SimpleStrategyWizard 
        open={open} 
        onOpenChange={(newOpenState) => {
          if (!newOpenState) {
            // If SimpleStrategyWizard is closed, also close the parent dialog
            onOpenChange(false);
          }
        }} 
        onSubmit={onSubmit}
        initialData={initialData}
      />
    );
  }
  
  if (dialogType === 'advanced') {
    // Directly use StepBasedStrategyDialog with ErrorBoundary as fallback protection
    return (
      <ErrorBoundary fallback={
        <SimpleStrategyWizard 
          open={open} 
          onOpenChange={(newOpenState) => {
            if (!newOpenState) {
              onOpenChange(false);
            }
          }}
          onSubmit={onSubmit}
          initialData={initialData}
        />
      }>
        <StepBasedStrategyDialog
          open={open}
          onOpenChange={(newOpenState) => {
            if (!newOpenState) {
              onOpenChange(false);
            }
          }}
          onSubmit={onSubmit}
          initialData={initialData}
          isStewieQuant={isStewieQuant}
        />
      </ErrorBoundary>
    );
  }
  
  // If we're at the chooser stage, show the dialog selection
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <div className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Strategy</DialogTitle>
          </DialogHeader>
          
          <div className="py-6 space-y-6">
            <p className="text-muted-foreground">
              Choose how you want to create your trading strategy:
            </p>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div 
                className="relative border rounded-lg p-5 cursor-default bg-gray-50"
              >
                <div className="absolute inset-0 flex items-center justify-center z-10 bg-gray-100/80 rounded-lg">
                  <div className="bg-gray-500 text-white py-1 px-3 rounded-full text-sm font-medium tracking-wide transform -rotate-6">
                    Coming Soon
                  </div>
                </div>
                
                <h3 className="font-medium mb-2 text-gray-500">Quick Create</h3>
                <p className="text-sm text-muted-foreground">
                  Simple form with minimal configuration. Get started quickly.
                </p>
                <ul className="mt-4 text-sm space-y-1">
                  <li className="flex items-center text-gray-500">
                    <span className="text-gray-400 mr-2">✓</span> Fast setup
                  </li>
                  <li className="flex items-center text-gray-500">
                    <span className="text-gray-400 mr-2">✓</span> Basic settings
                  </li>
                  <li className="flex items-center text-gray-500">
                    <span className="text-gray-400 mr-2">○</span> Limited customization
                  </li>
                </ul>
              </div>
              
              <div 
                className="relative border rounded-lg p-5 cursor-pointer hover:border-primary transition-colors"
                onClick={() => setDialogType('advanced')}
              >
                <h3 className="font-medium mb-2">Advanced Create</h3>
                <p className="text-sm text-muted-foreground">
                  Step-by-step wizard with full configuration options.
                </p>
                <ul className="mt-4 text-sm space-y-1">
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span> Multiple models
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span> Symbol baskets
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span> Advanced risk settings
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

